import React from "react";
import { zodResolver } from "@hookform/resolvers/zod";

import { z } from "zod";
import { useForm } from "react-hook-form";
import { categorySchema } from "@/utils/schemaHelper";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/uiComponents/components/ui/dialog";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/uiComponents/components/ui/form";
import { Input } from "@/uiComponents/components/ui/input";

import { useDispatch, useSelector } from "react-redux";

import { RootState } from "@/store/store";
import { setLoading } from "@/store/redux/appSlice";
import {
  createCategoryApi,
  getCategoryApi,
} from "@/modules/organizationServices";
import {
  addCategory,
  setCategories,
} from "@/store/redux/organization/categorySlice";

import { addCategoryProps } from "@/types/organization";
import { OrganizationType } from "@/types/apiResposne";

import { ErrorHelper } from "@/utils/errorHelper";
import { toastMessage } from "@/utils/toastHelper";
import { CategoryMessage } from "@/enums/errorMessages";
import { ReusableButton } from "@/reusable/reusableButton";

const AddCategory: React.FC<addCategoryProps> = ({
  onClose,

  isOpen,
}) => {
  if (!isOpen) return null;
  const dispatch = useDispatch();
  const loading = useSelector((state: RootState) => state.app.loading);
  const userDetails = useSelector(
    (state: RootState) => state.userDetail.userDetail,
  );

  const form = useForm<z.infer<typeof categorySchema>>({
    resolver: zodResolver(categorySchema),
    mode: "onChange",
    defaultValues: {
      name: "",
      organization: userDetails?.organizationId,
    },
  });

  const getCategories = async () => {
    try {
      const res = await getCategoryApi();
      const response = res.categories;
      if (response != undefined) {
        const categories: OrganizationType.CategoryResponse[] = Array.isArray(
          response,
        )
          ? response
          : [response];
        dispatch(setCategories(categories));
      }
    } catch (error) {
      const actualError = ErrorHelper(error);
      const message = Array.isArray(actualError) ? actualError[0] : actualError;
      toastMessage("error", message);
    }
  };

  const onSubmit = async (values: z.infer<typeof categorySchema>) => {
    if (loading) return;
    dispatch(setLoading(true));

    try {
      await createCategoryApi(values);
      getCategories();
      toastMessage("success", CategoryMessage.CATEGORY_CREATED);
      onClose();
      if (!userDetails?.organizationId) return;
      const categoryData = {
        id: Date.now(),
        organizationName: userDetails.organizationId,
        ...values,
      };
      dispatch(addCategory({ ...categoryData }));
    } catch (error) {
      const actualError = ErrorHelper(error);
      const message = Array.isArray(actualError) ? actualError[0] : actualError;
      toastMessage("error", message);
    } finally {
      dispatch(setLoading(false));
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Add Category</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-6 mt-5"
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>

                  <FormControl>
                    <Input
                      className="bg-white"
                      {...field}
                      placeholder="Enter category name"
                    />
                  </FormControl>

                  <FormMessage className="text-xs mt-1" />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="organization"
              render={({ field }) => (
                <FormItem>
                  <FormLabel hidden>Organization</FormLabel>
                  <FormControl>
                    <Input hidden {...field} />
                  </FormControl>
                </FormItem>
              )}
            />

            <div className="flex justify-center mt-4 mr-2">
              <ReusableButton
                label="Add"
                className=" mt-5 mb-2 "
                loading={loading}
              />
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default AddCategory;
